import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVolver } from '@/shared/hooks/useVolver';
import { productsApi } from '@/shared/api/endpoints';
import type { ProductMatch, ProductSearchResult } from '@/shared/api/types';
import { formatGrams } from '@/shared/format/nutrition';
import { describeError } from '@/shared/hooks/useAsync';
import { Banner, Button, Card, Eyebrow, Field } from '@/shared/ui';
import { BackIcon } from '@/shared/ui/icons';
import { EsqueletoLista, useEsqueleto } from '@/shared/ui/Skeleton';
import { OwnProductSheet } from './OwnProductSheet';

/** Por debajo de esto el resultado se muestra apagado y se dice que está bajo el umbral. */
const RELEVANCE_THRESHOLD = 0.75;

/**
 * S-06 · la búsqueda en el catálogo.
 *
 * Cada resultado dice **de qué señal vino**. Sin eso, "no aparece lo que busco" es indistinguible
 * de "la mitad semántica no corrió", y la persona no tiene forma de saber si conviene reformular
 * la consulta o cargar el producto a mano.
 */
export function ProductSearchPage() {
  const navigate = useNavigate();
  const volver = useVolver('/');
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<ProductSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const search = async () => {
    setSearching(true);
    setError(null);
    try {
      setResult(await productsApi.search(query));
    } catch (cause) {
      setError(describeError(cause));
      setResult(null);
    } finally {
      setSearching(false);
    }
  };

  const esqueleto = useEsqueleto(searching);

  return (
    <>
      <header className="row-between">
        <button
          type="button"
          className="btn-square"
          aria-label="Volver"
          onClick={volver}
        >
          <BackIcon size={18} />
        </button>
        <h1 style={{ fontSize: 'var(--text-h2)' }}>Buscar un producto</h1>
        <span style={{ width: 38 }} />
      </header>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void search();
        }}
      >
        <Field
          label="Qué buscás"
          autoFocus
          placeholder="Yogur bebible, galletitas de agua…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </form>

      {error !== null && <Banner tone="danger">{error}</Banner>}
      {esqueleto && <EsqueletoLista etiqueta="Buscando productos" filas={4} />}

      {result !== null && (
        <>
          <div className="row-between">
            <span className="micro muted">
              {result.matches.length} {result.matches.length === 1 ? 'resultado' : 'resultados'}
              {' · '}
              {/* RN-20 · el puntaje es el máximo entre la difusa y la semántica. */}
              {result.semanticApplied ? 'difusa + semántica' : 'sólo difusa'}
            </span>
            <Eyebrow>Puntaje</Eyebrow>
          </div>

          {!result.semanticApplied && (
            <p className="micro muted">
              La búsqueda por significado no está disponible en este momento: estos resultados son
              por parecido de nombre.
            </p>
          )}

          <Card size="flush">
            {result.matches.map((match, index) => (
              <ResultRow key={match.product.id} match={match} best={index === 0} />
            ))}
          </Card>
        </>
      )}

      {result !== null && (
        /* CU-12 A1 · el camino cuando nada sirve. Está siempre, no sólo cuando hay cero
           resultados: encontrar cuatro cosas parecidas y ninguna correcta es el caso frecuente. */
        <div className="card-dashed">
          <strong className="small">Ninguno es el que comiste</strong>
          <span className="micro muted">
            Podés fotografiar el rótulo y que los valores se carguen solos, o cargarlo a mano.
          </span>
          <div className="row">
            {/*
              A /productos/aportar y no a /capturar. Antes mandaba al flujo de comidas, que crea
              una comida: alguien que quería sumar un producto al catálogo terminaba registrando
              que se lo había comido.
            */}
            <Button variant="quiet" onClick={() => navigate('/productos/aportar')}>
              Fotografiar el rótulo
            </Button>
            <Button variant="quiet" onClick={() => setCreating(true)}>
              Cargar a mano
            </Button>
          </div>
        </div>
      )}

      {creating && (
        <OwnProductSheet
          initialName={query}
          onCreated={(product) =>
            setResult({
              matches: [{ product, score: 1, fuzzyScore: 1, semanticScore: null }],
              semanticApplied: result?.semanticApplied ?? false,
            })
          }
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

function ResultRow({ match, best }: { match: ProductMatch; best: boolean }) {
  const belowThreshold = match.score < RELEVANCE_THRESHOLD;

  // De qué señal vino: la que produjo el puntaje que ganó.
  const signal =
    match.semanticScore !== null && match.semanticScore > match.fuzzyScore ? 'semántica' : 'difusa';

  const { macros100g } = match.product;

  return (
    <div className="item-row" style={{ cursor: 'default', opacity: belowThreshold ? 0.72 : 1 }}>
      {/*
        La miniatura sólo aparece cuando hay foto. No se reserva el hueco ni se dibuja un
        marcador gris para las que no tienen: la mayor parte del catálogo son tablas de
        composición sin imagen, y una columna de rectángulos vacíos ocuparía lugar para
        informar que no hay nada.
      */}
      {match.product.photoUrl !== null && (
        <img
          className="item-thumb"
          src={match.product.photoUrl}
          alt=""
          loading="lazy"
          decoding="async"
        />
      )}

      <span className="item-body">
        <span className="item-name" style={{ fontSize: '0.84375rem' }}>
          {match.product.name}
        </span>
        {match.product.brand !== null && <span className="item-meta">{match.product.brand}</span>}
        <span className="item-meta">
          Por 100 g · {formatGrams(macros100g.proteinG)} P ·{' '}
          {formatGrams(macros100g.carbohydratesG)} C · {formatGrams(macros100g.fatG)} G
        </span>
      </span>

      <span className="item-value">
        <span className={best ? 'badge badge-done' : 'badge badge-neutral'}>
          {match.score.toFixed(2).replace('.', ',')}
        </span>
        <span className="nano muted">{belowThreshold ? 'bajo el umbral' : signal}</span>
      </span>
    </div>
  );
}
